import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Divider,
  Container,
  Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RealtimeIcon from '@mui/icons-material/Cable';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CategoryIcon from '@mui/icons-material/Category';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axios from 'axios';
import { useAuth } from '../hooks/auth/useAuth';

const Status = () => {
  // Auth context for authenticated requests
  const { user, token } = useAuth();
  
  // State for API status data
  const [apiStatus, setApiStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);
  const [wsStatus, setWsStatus] = useState('checking');
  const [overallHealth, setOverallHealth] = useState(null);

  // Base API URL from environment variable
  const baseApiUrl = process.env.REACT_APP_API_URL;

  // Comprehensive API endpoints organized by category
  const apiEndpointCategories = useMemo(() => [
    {
      category: 'Core Infrastructure',
      icon: <CloudIcon />,
      color: 'primary',
      critical: true,
      endpoints: [
        { name: 'Health Check', endpoint: '/health', public: true },
        { name: 'API Documentation', endpoint: '/api-docs', public: true },
      ]
    },
    {
      category: 'Authentication & Security',
      icon: <SecurityIcon />,
      color: 'secondary',
      critical: true,
      endpoints: [
        { name: 'Authentication', endpoint: '/auth/refresh', public: false },
        { name: 'Simple SSO', endpoint: '/sso/status', public: false },
      ]
    },
    {
      category: 'ENViS AI System',
      icon: <SmartToyIcon />,
      color: 'success',
      critical: true,
      endpoints: [
        { name: 'ENViS Devices', endpoint: '/envis-devices', public: false },
        { name: 'ENViS Findings', endpoint: '/envis-findings', public: false },
        { name: 'ENViS Payload Processor', endpoint: '/payload/stats', public: false },
        { name: 'Burst Findings', endpoint: '/burst-findings', public: false },
        { name: 'ENViS Device Endpoint', endpoint: '/envis/payload', public: true, method: 'OPTIONS' },
      ]
    },
    {
      category: 'Asset Management',
      icon: <BusinessIcon />,
      color: 'info',
      critical: true,
      endpoints: [
        { name: 'Organizations', endpoint: '/organizations', public: false },
        { name: 'Networks', endpoint: '/networks', public: false },
        { name: 'Sections', endpoint: '/sections', public: false },
        { name: 'Assets', endpoint: '/assets', public: false },
        { name: 'Defects', endpoint: '/defects', public: false },
        { name: 'Inspections', endpoint: '/inspections', public: false },
        { name: 'Work Orders', endpoint: '/work-orders', public: false },
        { name: 'Sensors', endpoint: '/sensors', public: false },
      ]
    },
    {
      category: 'Classification System',
      icon: <CategoryIcon />,
      color: 'warning',
      critical: false,
      endpoints: [
        { name: 'Asset Categories', endpoint: '/asset-categories', public: false },
        { name: 'Asset Types', endpoint: '/asset-types', public: false },
        { name: 'Defect Types', endpoint: '/defect-types', public: false },
        { name: 'Severity Schemes', endpoint: '/severity-schemes', public: false },
        { name: 'Bounding Boxes', endpoint: '/bounding-boxes', public: false },
      ]
    },
    {
      category: 'User & Contractor Management',
      icon: <PeopleIcon />,
      color: 'secondary',
      critical: false,
      endpoints: [
        { name: 'Users', endpoint: '/users', public: false },
        { name: 'Contractors', endpoint: '/contractors', public: false },
        { name: 'Contractor Work Orders', endpoint: '/contractor-work-orders', public: false },
      ]
    },
    {
      category: 'File & Media Management',
      icon: <FileUploadIcon />,
      color: 'info',
      critical: false,
      endpoints: [
        { name: 'Secure Uploads', endpoint: '/uploads', public: false },
        { name: 'File Uploads', endpoint: '/secure-uploads', public: false },
        { name: '3D Model Proxy', endpoint: '/3d-model-proxy', public: false },
      ]
    },
    {
      category: 'Reporting & Analytics',
      icon: <AssessmentIcon />,
      color: 'success',
      critical: false,
      endpoints: [
        { name: 'Reports', endpoint: '/reports', public: false },
      ]
    }
  ], []);

  // Check WebSocket connection status
  const checkWebSocketStatus = useCallback(() => {
    try {
      const wsUrl = baseApiUrl.replace('http', 'ws').replace('/api', '');
      const socket = new WebSocket(wsUrl);
      
      const timeout = setTimeout(() => {
        setWsStatus('offline');
        socket.close();
      }, 5000);

      socket.onopen = () => {
        clearTimeout(timeout);
        setWsStatus('online');
        socket.close();
      };

      socket.onerror = () => {
        clearTimeout(timeout);
        setWsStatus('offline');
      };
    } catch (error) {
      setWsStatus('offline');
    }
  }, [baseApiUrl]);

  // Check status of a single API endpoint
  const checkEndpoint = useCallback(async (endpoint, isPublic = false, method = 'GET') => {
    const startTime = new Date().getTime();
    try {
      const timestamp = new Date().getTime();
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };

      // Add authentication for protected endpoints
      if (!isPublic && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const config = {
        method: method,
        url: `${baseApiUrl}${endpoint}?_=${timestamp}`,
        timeout: 10000,
        validateStatus: false,
        headers
      };

      const response = await axios(config);
      const endTime = new Date().getTime();
      const responseTime = endTime - startTime;

      let status;
      if (response.status >= 200 && response.status < 300) {
        status = 'online';
      } else if (response.status === 401 && !isPublic) {
        status = 'auth_required';
      } else if (response.status >= 500) {
        status = 'offline';
      } else {
        status = 'degraded';
      }

      if (status === 'online' && responseTime > 2000) {
        status = 'degraded';
      }

      return {
        status,
        responseTime: `${responseTime}ms`,
        statusCode: response.status
      };
    } catch (error) {
      console.error(`Error checking ${endpoint}:`, error);
      return {
        status: 'offline',
        responseTime: '0ms',
        statusCode: error.code || 'Connection Error'
      };
    }
  }, [baseApiUrl, token]);

  // Check overall system health
  const checkOverallHealth = useCallback(async () => {
    try {
      const response = await axios.get(`${baseApiUrl}/health`, {
        timeout: 5000,
        validateStatus: false
      });

      if (response.status === 200) {
        setOverallHealth({
          status: 'healthy',
          data: response.data
        });
      } else {
        setOverallHealth({
          status: 'unhealthy',
          data: null
        });
      }
    } catch (error) {
      setOverallHealth({
        status: 'offline',
        data: null
      });
    }
  }, [baseApiUrl]);

  // Function to check all API statuses
  const checkApiStatus = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    setWsStatus('checking');
    
    // Initialize all endpoints as checking
    const allEndpoints = apiEndpointCategories.flatMap(category => 
      category.endpoints.map(endpoint => ({
        ...endpoint,
        category: category.category,
        critical: category.critical,
        status: 'checking',
        responseTime: '--',
        checking: true
      }))
    );
    
    setApiStatus(allEndpoints);

    try {
      // Check overall health first
      await checkOverallHealth();
      
      // Check WebSocket status
      checkWebSocketStatus();

      // Check each endpoint in parallel
      const promises = allEndpoints.map(async (endpoint) => {
        const result = await checkEndpoint(
          endpoint.endpoint, 
          endpoint.public, 
          endpoint.method || 'GET'
        );
        return {
          ...endpoint,
          ...result,
          checking: false
        };
      });

      const results = await Promise.all(promises);
      setApiStatus(results);
    } catch (error) {
      console.error('Error checking API status:', error);
      setError('An error occurred while checking API status. See console for details.');
    } finally {
      setLastChecked(new Date());
      setRefreshing(false);
      setLoading(false);
    }
  }, [apiEndpointCategories, checkEndpoint, checkOverallHealth, checkWebSocketStatus]);

  // Initial load
  useEffect(() => {
    checkApiStatus();
  }, [checkApiStatus]);

  // Render status chip based on status value
  const renderStatusChip = (status) => {
    switch(status) {
      case 'online':
        return <Chip 
          icon={<CheckCircleIcon />} 
          label="Online" 
          color="success" 
          size="small" 
          variant="outlined" 
        />;
      case 'offline':
        return <Chip 
          icon={<ErrorIcon />} 
          label="Offline" 
          color="error" 
          size="small" 
          variant="outlined" 
        />;
      case 'degraded':
        return <Chip 
          icon={<WarningIcon />} 
          label="Degraded" 
          color="warning" 
          size="small" 
          variant="outlined" 
        />;
      case 'auth_required':
        return <Chip 
          icon={<SecurityIcon />} 
          label="Auth Required" 
          color="info" 
          size="small" 
          variant="outlined" 
        />;
      case 'checking':
        return <Chip 
          icon={<CircularProgress size={12} />} 
          label="Checking" 
          color="default" 
          size="small" 
          variant="outlined" 
        />;
      default:
        return <Chip 
          label="Unknown" 
          color="default" 
          size="small" 
          variant="outlined" 
        />;
    }
  };

  // Calculate system health summary
  const getSystemHealthSummary = () => {
    if (apiStatus.length === 0) return { online: 0, total: 0, critical: 0 };
    
    const online = apiStatus.filter(api => api.status === 'online').length;
    const total = apiStatus.length;
    const criticalEndpoints = apiStatus.filter(api => api.critical);
    const criticalOnline = criticalEndpoints.filter(api => api.status === 'online').length;
    
    return { online, total, critical: criticalOnline, criticalTotal: criticalEndpoints.length };
  };

  const healthSummary = getSystemHealthSummary();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <HealthAndSafetyIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            System Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time monitoring of API endpoints and system health
            {user?.organizationName && ` - ${user.organizationName}`}
          </Typography>
        </Box>
      </Stack>

      {/* System Health Overview */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <HealthAndSafetyIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            System Health Overview
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  System Health
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: overallHealth?.status === 'healthy' ? 'success.main' : 'error.main',
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  {overallHealth?.status === 'healthy' ? '✓' : '✗'}
                </Typography>
                <Chip 
                  label={overallHealth?.status || 'Checking...'}
                  color={overallHealth?.status === 'healthy' ? 'success' : 'error'}
                  variant="outlined"
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  API Endpoints
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: healthSummary.online === healthSummary.total ? 'success.main' : 'warning.main',
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  {healthSummary.online}/{healthSummary.total}
                </Typography>
                <Chip 
                  label="Online"
                  color={healthSummary.online === healthSummary.total ? 'success' : 'warning'}
                  variant="outlined"
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Critical Services
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: healthSummary.critical === healthSummary.criticalTotal ? 'success.main' : 'error.main',
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  {healthSummary.critical}/{healthSummary.criticalTotal}
                </Typography>
                <Chip 
                  label="Operational"
                  color={healthSummary.critical === healthSummary.criticalTotal ? 'success' : 'error'}
                  variant="outlined"
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Real-time Connection
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  {renderStatusChip(wsStatus)}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  WebSocket Status
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Header */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
              Detailed API Status Monitor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitoring API endpoints at: <code>{baseApiUrl}</code>
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            {lastChecked && (
              <Typography variant="body2" color="text.secondary">
                Last checked: {lastChecked.toLocaleTimeString()}
              </Typography>
            )}
            <Button 
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={checkApiStatus}
              disabled={refreshing}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh All'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!user && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          You are not authenticated. Some protected endpoints will show "Auth Required" status.
        </Alert>
      )}

      {/* API Status Details */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <RealtimeIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            API Endpoint Status ({apiStatus.length} endpoints)
          </Typography>
        </Stack>
        
        {loading && apiStatus.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Checking API endpoint status...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Categorized Endpoints */}
            {apiEndpointCategories.map((category, categoryIndex) => {
              const categoryEndpoints = apiStatus.filter(api => api.category === category.category);
              const onlineCount = categoryEndpoints.filter(api => api.status === 'online').length;
              
              return (
                <Accordion 
                  key={categoryIndex} 
                  defaultExpanded={category.critical}
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: 2,
                      '&.Mui-expanded': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: `${category.color}.light`, 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {React.cloneElement(category.icon, { 
                          sx: { fontSize: 20, color: `${category.color}.main` } 
                        })}
                      </Box>
                      <Typography variant="h6" fontWeight={600}>
                        {category.category}
                      </Typography>
                      {category.critical && (
                        <Chip 
                          label="Critical" 
                          color="error" 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                      <Box sx={{ ml: 'auto' }}>
                        <Chip 
                          label={`${onlineCount}/${categoryEndpoints.length} Online`}
                          color={onlineCount === categoryEndpoints.length ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Service Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Endpoint</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Response Time</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status Code</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Access</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryEndpoints.map((api, index) => (
                            <TableRow 
                              key={index}
                              sx={{ 
                                '&:hover': {
                                  backgroundColor: 'grey.50',
                                }
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {api.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    bgcolor: 'grey.100',
                                    p: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {baseApiUrl}{api.endpoint}
                                </Typography>
                              </TableCell>
                              <TableCell>{renderStatusChip(api.status)}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {api.responseTime}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {api.statusCode}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={api.public ? 'Public' : 'Protected'} 
                                  color={api.public ? 'default' : 'primary'} 
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Status; 